import { useState, useMemo, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { bakimHesapla } from '@/pages/bakim-takip/utils/bakimHesapla';
import FiltreSec from './components/FiltreSecSupabase';
import MesajEditor from './components/MesajEditor';
import MusteriListesiSupabase from './components/MusteriListesiSupabase';

export type MesajFiltre = 'hepsi' | 'sari' | 'kirmizi';

interface MusteriMesaj {
  id: string;
  ad: string;
  soyad: string;
  telefon: string;
  ilce: string;
  son_bakim_tarihi: string | null;
}

export default function MesajGonderPage() {
  const [musteriler, setMusteriler] = useState<MusteriMesaj[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState<MesajFiltre>('kirmizi');
  const [mesaj, setMesaj] = useState('Sayın [MÜŞTERİ ADI], su arıtma cihazınızın bakımı kritik seviyeyi geçmiştir. Lütfen en kısa sürede servis randevusu alınız. Sadem Su Arıtma - 0212 XXX XX XX');
  const [seciliIds, setSeciliIds] = useState<Set<string>>(new Set());
  const [gonderimSonuc, setGonderimSonuc] = useState<{ basarili: number; kanal: string } | null>(null);
  const [gonderiyor, setGonderiyor] = useState(false);

  useEffect(() => {
    const fetchMusteriler = async () => {
      const { data: mData } = await supabase
        .from('sadem_musteriler')
        .select('id, ad, soyad, telefon, ilce')
        .eq('aktif', true).eq('silindi', false);

      const { data: bData } = await supabase
        .from('sadem_bakim_takibi')
        .select('musteri_id, son_bakim_tarihi')
        .eq('aktif', true)
        .order('son_bakim_tarihi', { ascending: false });

      const bakimMap: Record<string, string> = {};
      (bData || []).forEach((b: any) => {
        if (!bakimMap[b.musteri_id]) bakimMap[b.musteri_id] = b.son_bakim_tarihi;
      });

      setMusteriler((mData || []).map((m: any) => ({
        id: m.id, ad: m.ad, soyad: m.soyad, telefon: m.telefon, ilce: m.ilce || '',
        son_bakim_tarihi: bakimMap[m.id] || null,
      })));
      setLoading(false);
    };
    fetchMusteriler();
  }, []);

  const filtrelenmis = useMemo(() => {
    if (filtre === 'hepsi') return musteriler;
    return musteriler.filter(m => {
      if (!m.son_bakim_tarihi) return false;
      return bakimHesapla(m.son_bakim_tarihi).durum === filtre;
    });
  }, [musteriler, filtre]);

  const handleFiltre = (f: MesajFiltre) => {
    setFiltre(f);
    setSeciliIds(new Set());
    setGonderimSonuc(null);
  };

  const handleToggle = (id: string) => {
    setSeciliIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setGonderimSonuc(null);
  };

  const handleToggleAll = (val: boolean) => {
    setSeciliIds(val ? new Set(filtrelenmis.map(c => c.id)) : new Set());
    setGonderimSonuc(null);
  };

  const handleWhatsapp = async () => {
    const seciliMusteriler = filtrelenmis.filter(c => seciliIds.has(c.id));
    if (seciliMusteriler.length === 0) return;
    setGonderiyor(true);

    const ilk = seciliMusteriler[0];
    const kisiselMesaj = mesaj.replace('[MÜŞTERİ ADI]', `${ilk.ad} ${ilk.soyad}`);
    const numara = ilk.telefon.replace(/\s/g, '').replace(/^0/, '90');
    const url = `https://wa.me/${numara}?text=${encodeURIComponent(kisiselMesaj)}`;
    window.open(url, '_blank');

    // Log the messages to history
    try {
      for (const m of seciliMusteriler) {
        const kisisel = mesaj.replace('[MÜŞTERİ ADI]', `${m.ad} ${m.soyad}`);
        await supabase.from('sadem_mesaj_gecmisi').insert({
          musteri_id: m.id,
          telefon: m.telefon,
          mesaj_turu: 'whatsapp',
          hazirlanan_mesaj: kisisel,
          durum: 'whatsapp_acildi',
          whatsapp_acilma_tarihi: new Date().toISOString(),
        });
      }
    } catch {
      // Mesaj geçmişine kayıt başarısız olursa sessizce devam et
    }

    setGonderiyor(false);
    setGonderimSonuc({ basarili: seciliMusteriler.length, kanal: 'whatsapp' });
  };

  const mesajGecerli = mesaj.trim().length > 0 && mesaj.trim().length <= 500;
  const gonderilebilir = mesajGecerli && seciliIds.size > 0 && !gonderiyor;

  if (loading) {
    return (
      <div className="text-center py-16">
        <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
        <p className="text-sm text-gray-500 mt-3">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold text-gray-800">Toplu Mesaj Gönder</h2>
        <p className="text-sm text-gray-500 mt-0.5">Müşteri grubunu seçin, mesajı yazın ve WhatsApp ile gönderin.</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 flex items-center justify-center bg-sky-600 text-white text-xs font-bold rounded-full flex-shrink-0">1</div>
          <h3 className="text-sm font-bold text-gray-800">Müşteri Grubunu Seçin</h3>
        </div>
        <FiltreSec customers={musteriler} secili={filtre} onChange={handleFiltre} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-sky-600 text-white text-xs font-bold rounded-full flex-shrink-0">2</div>
            <h3 className="text-sm font-bold text-gray-800">Alıcıları Seçin</h3>
          </div>
          {seciliIds.size > 0 && <span className="text-xs bg-sky-100 text-sky-700 font-bold px-3 py-1 rounded-full">{seciliIds.size} kişi seçildi</span>}
        </div>
        <MusteriListesiSupabase customers={filtrelenmis} seciliIds={seciliIds} onToggle={handleToggle} onToggleAll={handleToggleAll} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 flex items-center justify-center bg-sky-600 text-white text-xs font-bold rounded-full flex-shrink-0">3</div>
          <h3 className="text-sm font-bold text-gray-800">Mesajı Hazırlayın</h3>
        </div>
        <MesajEditor mesaj={mesaj} onChange={setMesaj} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-6 h-6 flex items-center justify-center bg-sky-600 text-white text-xs font-bold rounded-full flex-shrink-0">4</div>
          <h3 className="text-sm font-bold text-gray-800">Gönder</h3>
        </div>

        {seciliIds.size > 0 && mesajGecerli && (
          <div className="bg-slate-50 rounded-xl px-4 py-3 mb-5 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-user-3-line text-gray-400"></i></div>
              <span className="text-gray-600"><span className="font-bold text-gray-800">{seciliIds.size}</span> alıcı</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-chat-3-line text-gray-400"></i></div>
              <span className="text-gray-600"><span className="font-bold text-gray-800">{mesaj.length}</span> karakter</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={handleWhatsapp} disabled={!gonderilebilir}
            className={`flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap
              ${gonderilebilir ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
            {gonderiyor ? <><i className="ri-loader-4-line animate-spin text-xl"></i> Gönderiliyor...</>
              : <><i className="ri-whatsapp-line text-xl"></i><span>WhatsApp ile Gönder</span></>}
          </button>
        </div>
      </div>

      {gonderimSonuc && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-emerald-600 text-xl"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-emerald-800">WhatsApp gönderimi başlatıldı!</p>
            <p className="text-xs text-emerald-600 mt-0.5">{gonderimSonuc.basarili} kişiye mesaj için WhatsApp penceresi açıldı.</p>
          </div>
          <button onClick={() => setGonderimSonuc(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-600 cursor-pointer flex-shrink-0">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
    </div>
  );
}